import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { Button, Dialog } from '@bloo-team/ui'
import { monthDayTime } from '../../lib/local-time'
import { heatLevel, likelihoodBand, mitreSummary, type MitreRow, type MitreTechnique } from '../../lib/signal-attribution'
import { tacticKey } from '@bloo-team/api-client'
import type { SubgraphSignal } from '../../lib/signal-subgraphs'

/**
 * The storyline drawer's MITRE tab (docs/signals.md §Storyline panel · MITRE).
 * The drawer is narrow, so the tab is a summary: a strip of every ATT&CK
 * tactic (lit where the storyline has a technique), the coverage count, and
 * the most-signalled techniques. Expand opens the full matrix in a dialog:
 * one column per tactic, a cell per technique — tinted by the selected
 * campaigns' likelihood band, its bar the signal count against the busiest
 * technique (heat). Pick a cell for its campaigns and the storyline's signals
 * with that technique; a signal opens in the pane.
 */

const cellKey = (tactic: string, name: string) => `${tacticKey(tactic)}::${name}`

function titleOf(t: MitreTechnique) {
  return t.campaigns.length ? `${t.campaigns.join(', ')} · ${Math.round(t.likelihood ?? 0)}%` : 'No selected campaign explains it'
}

/** A technique as the drawer has always drawn it: band tint, solid when seen, ×N, likelihood. */
function TechniqueChip({ t }: { t: MitreTechnique }) {
  return (
    <span
      className="sl-technique"
      data-band={t.likelihood !== null ? likelihoodBand(t.likelihood) : undefined}
      data-observed={t.signals > 0 || undefined}
      title={titleOf(t)}
    >
      {t.name}
      {t.signals > 1 ? <span className="mono"> ×{t.signals}</span> : null}
      {t.likelihood !== null ? <span className="mono"> {Math.round(t.likelihood)}%</span> : null}
    </span>
  )
}

export function StorylineMitre({
  rows,
  tacticOrder,
  selectedCampaigns,
  signals,
  openUuid,
  onOpenSignal,
}: {
  rows: MitreRow[]
  tacticOrder: readonly string[]
  /** How many campaigns are toggled on (the colouring note). */
  selectedCampaigns: number
  signals: readonly SubgraphSignal[]
  openUuid: string | null
  onOpenSignal: (uuid: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [pick, setPick] = useState<string | null>(null)
  const sum = useMemo(() => mitreSummary(rows, tacticOrder), [rows, tacticOrder])

  if (!rows.length) return <p className="q-facet-note">No classified techniques on this storyline.</p>

  const techniqueCount = rows.reduce((n, r) => n + r.techniques.length, 0)
  const lit = sum.tactics.filter((t) => t.techniques.length > 0).map((t) => t.tactic)
  const picked = pick
    ? sum.tactics.flatMap((c) => c.techniques.filter((t) => cellKey(c.tactic, t.name) === pick).map((t) => ({ ...t, tactic: c.tactic })))[0]
    : undefined
  const pickedSignals = picked
    ? signals.filter((s) => tacticKey(s.tactic) === tacticKey(picked.tactic) && s.technique === picked.name).sort((a, b) => (b.end ?? 0) - (a.end ?? 0))
    : []
  const note = `${
    selectedCampaigns
      ? `Coloured by the highest likelihood among ${selectedCampaigns} selected campaign${selectedCampaigns === 1 ? '' : 's'}.`
      : 'No campaign selected — techniques show uncoloured.'
  } Solid: seen in this storyline · dashed: named only by a selected campaign.`

  return (
    <div className="hud-stack sl-mitre-sum">
      <div className="hud-row sl-mitre-head">
        <span className="sl-tactic-strip" role="img" aria-label={`ATT&CK tactics: ${sum.covered} of ${sum.tactics.length} — ${lit.join(', ')}`}>
          {sum.tactics.map((t) => (
            <span key={t.tactic} data-on={t.techniques.length > 0 || undefined} title={`${t.tactic}${t.signals ? ` · ${t.signals} signal${t.signals === 1 ? '' : 's'}` : ''}`} />
          ))}
        </span>
        <span className="hud-unit mono">
          {sum.covered}/{sum.tactics.length} tactics · {techniqueCount} technique{techniqueCount === 1 ? '' : 's'}
        </span>
        <Button variant="outline" size="sm" tone="neutral" onPress={() => setOpen(true)}>
          Expand matrix
        </Button>
      </div>
      {sum.top.length ? (
        <div className="hud-stack">
          <span className="hud-eyebrow">most signalled</span>
          <span className="sl-techniques" role="list" aria-label="Most signalled techniques">
            {sum.top.map((t) => (
              <span key={cellKey(t.tactic, t.name)} role="listitem">
                <TechniqueChip t={t} />
              </span>
            ))}
          </span>
        </div>
      ) : null}
      <p className="q-facet-note">{note}</p>

      <Dialog open={open} onClose={() => setOpen(false)} title="MITRE ATT&CK · this storyline" className="sl-matrix-dialog">
        <p className="q-facet-note">{note} The bar under a technique is its signal count against the busiest one.</p>
        <div className="sl-matrix-wrap">
          <div className="sl-matrix" role="list" aria-label="ATT&CK matrix" style={{ '--sl-cols': sum.tactics.length } as CSSProperties}>
            {sum.tactics.map((c) => (
              <section key={c.tactic} role="listitem" className="sl-matrix-col" aria-label={c.tactic} data-on={c.techniques.length > 0 || undefined}>
                <span className="sl-matrix-head">
                  <span className="hud-eyebrow">{c.tactic}</span>
                  <span className="hud-unit mono">{c.techniques.length || '–'}</span>
                </span>
                {c.techniques.map((t) => {
                  const k = cellKey(c.tactic, t.name)
                  const heat = heatLevel(t.signals, sum.maxSignals)
                  return (
                    <button
                      key={k}
                      type="button"
                      className="sl-technique sl-cell"
                      data-band={t.likelihood !== null ? likelihoodBand(t.likelihood) : undefined}
                      data-observed={t.signals > 0 || undefined}
                      aria-pressed={pick === k}
                      title={titleOf(t)}
                      onClick={() => setPick((p) => (p === k ? null : k))}
                    >
                      <span className="sl-cell-name">{t.name}</span>
                      <span className="sl-cell-meta mono">
                        {t.signals ? `${t.signals} sig.` : 'campaign'}
                        {t.likelihood !== null ? ` · ${Math.round(t.likelihood)}%` : ''}
                      </span>
                      <span className="sl-bar" aria-hidden="true">
                        <span style={{ width: `${heat * 25}%` }} />
                      </span>
                    </button>
                  )
                })}
              </section>
            ))}
          </div>
        </div>
        {picked ? (
          <section className="hud-stack sl-cell-detail" aria-label={`Technique ${picked.name}`}>
            <span className="hud-eyebrow">
              {picked.tactic} › {picked.name}
            </span>
            <span className="hud-unit">
              {picked.signals ? `${picked.signals} signal${picked.signals === 1 ? '' : 's'} in this storyline` : 'Not seen in this storyline'}
              {picked.campaigns.length ? ` · ${picked.campaigns.join(', ')} (${Math.round(picked.likelihood ?? 0)}%)` : ' · no selected campaign names it'}
            </span>
            {pickedSignals.length ? (
              <ol className="sp-timeline">
                {pickedSignals.map((s) => (
                  <li key={s.uuid}>
                    <button
                      type="button"
                      className="sp-tl-row"
                      aria-current={openUuid === s.uuid ? 'true' : undefined}
                      onClick={() => {
                        setOpen(false)
                        onOpenSignal(s.uuid)
                      }}
                    >
                      <span className="sp-tl-time">{s.end !== null ? monthDayTime(s.end) : '—'}</span>
                      <span className="sp-sev" data-severity={s.severity} />
                      <span className="sp-tl-name">{s.detectionName || 'signal'}</span>
                      <span className="sp-tl-ents">{s.count > 1 ? `×${s.count}` : ''}</span>
                    </button>
                  </li>
                ))}
              </ol>
            ) : null}
          </section>
        ) : (
          <p className="hud-unit">Pick a technique for its campaigns and signals.</p>
        )}
      </Dialog>
    </div>
  )
}
